import { FormEvent, useEffect, useState } from "react";
import {
  apiCreateCategory,
  apiDeleteCategory,
  apiListCategories,
  apiUpdateCategory,
  type CategoryRow,
} from "../api";

export function CategoriesPage() {
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"income" | "expense">("expense");
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const { categories } = await apiListCategories();
    setRows(categories);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiCreateCategory({ name: name.trim(), kind });
      setName("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this category?")) return;
    setError(null);
    try {
      await apiDeleteCategory(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    }
  }

  if (loading) {
    return <p className="muted">Loading categories…</p>;
  }

  return (
    <div className="stack">
      <div className="card">
        <h2 className="mt-0">Categories</h2>
        <p className="muted">
          Group income and spending. Amounts you enter on transactions are treated as expenses or income
          based on the category. Set an optional <strong>color</strong> per category (used on the Reports pie chart;
          leave on Auto for the default palette).
        </p>
        {error ? <p className="error">{error}</p> : null}

        <form className="form-row" onSubmit={onCreate}>
          <label>
            Name
            <input
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="e.g. Groceries"
              required
            />
          </label>
          <label>
            Kind
            <select value={kind} onChange={(e) => setKind(e.currentTarget.value as "income" | "expense")}>
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
          </label>
          <button type="submit" disabled={busy}>
            Add category
          </button>
        </form>
      </div>

      <div className="card">
        <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Color</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="muted">
                  No categories yet.
                </td>
              </tr>
            ) : (
              rows.map((c) => (
                <CategoryRowEditor key={c.id} category={c} onSaved={refresh} onDelete={() => remove(c.id)} />
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

const COLOR_PICKER_FALLBACK = "#64748b";

function categoryColorInputValue(color: string | null): string {
  return color && /^#[0-9a-fA-F]{6}$/i.test(color) ? color : COLOR_PICKER_FALLBACK;
}

function CategoryRowEditor({
  category,
  onSaved,
  onDelete,
}: {
  category: CategoryRow;
  onSaved: () => Promise<void>;
  onDelete: () => void;
}) {
  const [name, setName] = useState(category.name);
  const [kind, setKind] = useState(category.kind);

  useEffect(() => {
    setName(category.name);
    setKind(category.kind);
  }, [category.id, category.name, category.kind]);

  async function saveName() {
    await apiUpdateCategory(category.id, { name: name.trim() });
    await onSaved();
  }

  async function saveColor(hex: string | null) {
    await apiUpdateCategory(category.id, { color: hex });
    await onSaved();
  }

  return (
    <tr>
      <td>
        <input
          className="input-inline"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onBlur={() => void saveName()}
        />
      </td>
      <td>
        <select
          value={kind}
          onChange={(e) => {
            const k = e.currentTarget.value;
            setKind(k);
            void (async () => {
              await apiUpdateCategory(category.id, { kind: k });
              await onSaved();
            })();
          }}
        >
          <option value="expense">expense</option>
          <option value="income">income</option>
        </select>
      </td>
      <td>
        <div className="category-color-cell">
          <input
            type="color"
            className="input-color"
            aria-label={`Color for ${category.name}`}
            value={categoryColorInputValue(category.color)}
            onChange={(e) => void saveColor(e.currentTarget.value)}
          />
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={category.color == null}
            onClick={() => void saveColor(null)}
          >
            Auto
          </button>
        </div>
      </td>
      <td className="right">
        <button type="button" className="btn-ghost btn-sm" onClick={onDelete}>
          Delete
        </button>
      </td>
    </tr>
  );
}
